/**
 * CategoryRefinementAccordion Component
 *
 * Hierarchical category selection accordion for search filters.
 * Displays L1 → L2 → L3 category hierarchy with experience counts.
 *
 * Features:
 * - Expandable accordion structure (L1 → L2 → L3)
 * - Multi-select at any level
 * - Experience counts for each category
 * - Responsive design (desktop dropdown / mobile inline)
 * - WCAG 2.1 AA compliant
 *
 * Architecture:
 * - Uses taxonomyService for API calls
 * - Per-branch hierarchical selection (L3 > L2 > L1 specificity)
 * - Selection state: { l1Id, l2Id, l3Id, name, slug } objects
 * - Parent component handles URL state via onChange callback
 *
 * Usage:
 * <CategoryRefinementAccordion
 *   value={[{ l3Id: 5, name: 'Bass Fishing', slug: 'bass-fishing' }]}
 *   onChange={(selections) => updateFilter('categories', selections)}
 *   variant="dropdown" // or "inline"
 *   onClose={() => setShowDropdown(false)}
 * />
 *
 * Location: apps/web/src/components/search/CategoryRefinementAccordion.jsx
 * Created: 2025-11-26
 * Part of: MVP 1.3 Phase 2 - Search Taxonomy & SEO
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { getCompleteTaxonomy } from '../../services/taxonomyService';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * CategoryRefinementAccordion Component
 * @param {Array} value - Selected categories: [{ l1Id?, l2Id?, l3Id?, name, slug }, ...]
 * @param {Function} onChange - Callback when selection changes: onChange(categories)
 * @param {string} variant - Display variant: 'dropdown' (desktop) or 'inline' (mobile)
 * @param {Function} onClose - Callback to close dropdown (for dropdown variant)
 */
export default function CategoryRefinementAccordion({
  value = [],
  onChange = () => {},
  variant = 'dropdown',
  onClose: _onClose = () => {}
}) {
  const theme = useTheme();

  // State
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedL1, setExpandedL1] = useState(new Set()); // Expanded L1 category IDs
  const [expandedL2, setExpandedL2] = useState(new Set()); // Expanded L2 category IDs

  // Normalize value to array
  const selectedCategories = Array.isArray(value) ? value : [];

  // Build lookup sets for O(1) selection checks
  const selectedL1Ids = new Set(selectedCategories.filter(c => c.l1Id && !c.l2Id && !c.l3Id).map(c => c.l1Id));
  const selectedL2Ids = new Set(selectedCategories.filter(c => c.l2Id && !c.l3Id).map(c => c.l2Id));
  const selectedL3Ids = new Set(selectedCategories.filter(c => c.l3Id).map(c => c.l3Id));

  /**
   * Load taxonomy on mount
   */
  useEffect(() => {
    loadTaxonomy();
  }, []);

  /**
   * Fetch complete taxonomy from API
   */
  const loadTaxonomy = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCompleteTaxonomy({ includeCounts: true });
      setTaxonomy(data);
    } catch (err) {
      console.error('[CategoryRefinementAccordion] Failed to load taxonomy:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle L1 expansion
   */
  const toggleL1 = (l1Id) => {
    const newExpanded = new Set(expandedL1);
    if (newExpanded.has(l1Id)) {
      newExpanded.delete(l1Id);
    } else {
      newExpanded.add(l1Id);
    }
    setExpandedL1(newExpanded);
  };

  /**
   * Toggle L2 expansion
   */
  const toggleL2 = (l2Id) => {
    const newExpanded = new Set(expandedL2);
    if (newExpanded.has(l2Id)) {
      newExpanded.delete(l2Id);
    } else {
      newExpanded.add(l2Id);
    }
    setExpandedL2(newExpanded);
  };

  /**
   * Handle L1 category selection
   * Selecting L1 means "all experiences in this L1 category"
   */
  const handleL1Select = (l1Category) => {
    const isSelected = selectedL1Ids.has(l1Category.id);

    if (isSelected) {
      // Deselect: Remove this L1 and all its children
      const updated = selectedCategories.filter(
        c => c.l1Id !== l1Category.id
      );
      onChange(updated);
    } else {
      // Select: Add L1 (remove any child L2/L3 selections for this L1)
      const updated = [
        ...selectedCategories.filter(c => c.l1Id !== l1Category.id),
        {
          l1Id: l1Category.id,
          name: l1Category.name,
          slug: l1Category.slug
        }
      ];
      onChange(updated);
    }
  };

  /**
   * Handle L2 category selection
   * Selecting L2 means "all experiences in this L2 subcategory"
   */
  const handleL2Select = (l1Category, l2Category) => {
    const isSelected = selectedL2Ids.has(l2Category.id);

    if (isSelected) {
      // Deselect: Remove this L2 and all its L3 children
      const updated = selectedCategories.filter(
        c => c.l2Id !== l2Category.id
      );
      onChange(updated);
    } else {
      // Select: Add L2 (remove any child L3 selections and parent L1 selection)
      const updated = [
        ...selectedCategories.filter(
          c => c.l1Id !== l1Category.id || (c.l2Id && c.l2Id !== l2Category.id)
        ),
        {
          l1Id: l1Category.id,
          l2Id: l2Category.id,
          name: l2Category.name,
          slug: l2Category.slug
        }
      ];
      onChange(updated);
    }
  };

  /**
   * Handle L3 category selection
   * Selecting L3 means "experiences of this specific service type"
   */
  const handleL3Select = (l1Category, l2Category, l3Category) => {
    const isSelected = selectedL3Ids.has(l3Category.id);

    if (isSelected) {
      // Deselect: Remove this L3
      const updated = selectedCategories.filter(
        c => c.l3Id !== l3Category.id
      );
      onChange(updated);
    } else {
      // Select: Add L3 (remove parent L2 or L1 selections in this branch)
      const updated = [
        ...selectedCategories.filter(
          c => !(c.l1Id === l1Category.id && (!c.l2Id || c.l2Id === l2Category.id))
        ),
        {
          l1Id: l1Category.id,
          l2Id: l2Category.id,
          l3Id: l3Category.id,
          name: l3Category.name,
          slug: l3Category.slug
        }
      ];
      onChange(updated);
    }
  };

  /**
   * Clear all selections
   */
  const handleClear = () => {
    onChange([]);
  };

  /**
   * Render checkbox UI
   */
  const renderCheckbox = (isSelected) => {
    return (
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
          isSelected
            ? `${theme.borderPrimary} ${theme.buttonPrimary.split(' ')[0]}`
            : 'border-gray-300'
        }`}
      >
        {isSelected && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    );
  };

  /**
   * Render experience count badge
   */
  const renderCount = (count) => {
    if (!count) {return null;}
    return (
      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
        {count}
      </span>
    );
  };

  /**
   * Render L3 service type
   */
  const renderL3 = (l1Category, l2Category, l3Category) => {
    const isSelected = selectedL3Ids.has(l3Category.id);

    return (
      <button
        key={l3Category.id}
        onClick={() => handleL3Select(l1Category, l2Category, l3Category)}
        className={`flex items-center gap-2 p-2 pl-16 rounded transition hover:bg-gray-50 w-full text-left ${
          isSelected ? theme.bgLight : ''
        }`}
        aria-label={`Select ${l3Category.name}`}
      >
        <span
          className={`text-sm flex-1 ${
            isSelected ? theme.textPrimary + ' font-medium' : 'text-gray-600'
          }`}
        >
          {l3Category.name}
        </span>
        {renderCount(l3Category.experience_count)}
        {renderCheckbox(isSelected)}
      </button>
    );
  };

  /**
   * Render L2 subcategory
   */
  const renderL2 = (l1Category, l2Category) => {
    const isSelected = selectedL2Ids.has(l2Category.id);
    const hasL3Children = l2Category.children && l2Category.children.length > 0;
    const isExpanded = expandedL2.has(l2Category.id);

    return (
      <div key={l2Category.id} className="border-l-2 border-gray-200 ml-6">
        <div className="flex items-center">
          {/* Expand/collapse button (only if has L3 children) */}
          {hasL3Children && (
            <button
              onClick={() => toggleL2(l2Category.id)}
              className="p-2 hover:bg-gray-50 rounded transition"
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${l2Category.name}`}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}
          {!hasL3Children && <div className="w-10" />}

          {/* L2 category selection */}
          <button
            onClick={() => handleL2Select(l1Category, l2Category)}
            className={`flex items-center gap-2 p-2 rounded transition hover:bg-gray-50 flex-1 text-left ${
              isSelected ? theme.bgLight : ''
            }`}
            aria-label={`Select ${l2Category.name}`}
          >
            <span
              className={`text-sm font-medium flex-1 ${
                isSelected ? theme.textPrimary : 'text-gray-700'
              }`}
            >
              {l2Category.name}
            </span>
            {renderCount(l2Category.experience_count)}
            {renderCheckbox(isSelected)}
          </button>
        </div>

        {/* L3 children (if expanded) */}
        {isExpanded && hasL3Children && (
          <div className="space-y-1 mt-1">
            {l2Category.children.map(l3 => renderL3(l1Category, l2Category, l3))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render L1 top-level category
   */
  const renderL1 = (l1Category) => {
    const isSelected = selectedL1Ids.has(l1Category.id);
    const hasL2Children = l1Category.children && l1Category.children.length > 0;
    const isExpanded = expandedL1.has(l1Category.id);

    return (
      <div key={l1Category.id} className="border-b border-gray-200 last:border-b-0">
        <div className="flex items-center">
          {/* Expand/collapse button (only if has L2 children) */}
          {hasL2Children && (
            <button
              onClick={() => toggleL1(l1Category.id)}
              className="p-3 hover:bg-gray-50 rounded transition"
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${l1Category.name}`}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronDown className={`w-5 h-5 ${theme.textPrimary}`} />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
          )}
          {!hasL2Children && <div className="w-11" />}

          {/* L1 category selection */}
          <button
            onClick={() => handleL1Select(l1Category)}
            className={`flex items-center gap-3 p-3 rounded-lg transition hover:bg-gray-50 flex-1 text-left ${
              isSelected ? theme.bgLight : ''
            }`}
            aria-label={`Select ${l1Category.name}`}
          >
            {l1Category.icon_name && (
              <span className="text-2xl flex-shrink-0">{l1Category.icon_name}</span>
            )}
            <span
              className={`text-base font-semibold flex-1 ${
                isSelected ? theme.textPrimary : 'text-gray-900'
              }`}
            >
              {l1Category.name}
            </span>
            {renderCount(l1Category.experience_count)}
            {renderCheckbox(isSelected)}
          </button>
        </div>

        {/* L2 children (if expanded) */}
        {isExpanded && hasL2Children && (
          <div className="space-y-1 py-2">
            {l1Category.children.map(l2 => renderL2(l1Category, l2))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
          {error}
        </div>
        <button
          onClick={loadTaxonomy}
          className={`mt-3 text-sm ${theme.textPrimary} ${theme.textHover} font-medium`}
        >
          Try Again
        </button>
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (!taxonomy || !taxonomy.categories || taxonomy.categories.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        No categories available
      </div>
    );
  }

  /**
   * Render category accordion
   */
  const renderAccordion = () => {
    return (
      <div className="space-y-0">
        {taxonomy.categories.map(l1 => renderL1(l1))}
      </div>
    );
  };

  // Dropdown variant (desktop)
  if (variant === 'dropdown') {
    return (
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 min-w-96 max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-semibold text-gray-900">Select Adventure Categories</h3>
          {selectedCategories.length > 0 && (
            <button
              onClick={handleClear}
              className={`text-xs ${theme.textPrimary} ${theme.textHover} font-medium`}
              aria-label="Clear all selections"
            >
              Clear ({selectedCategories.length})
            </button>
          )}
        </div>

        <div className="max-h-[32rem] overflow-y-auto">
          {renderAccordion()}
        </div>
      </div>
    );
  }

  // Inline variant (mobile)
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {selectedCategories.length > 0 && (
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-700">
            {selectedCategories.length} selected
          </span>
          <button
            onClick={handleClear}
            className={`text-xs ${theme.textPrimary} ${theme.textHover} font-medium`}
            aria-label="Clear all selections"
          >
            Clear
          </button>
        </div>
      )}
      <div className="max-h-96 overflow-y-auto">
        {renderAccordion()}
      </div>
    </div>
  );
}
